import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import SEO from "../components/SEO";
import BadgeTier from "../components/BadgeTier";
import StarRating from "../components/StarRating";
import api, { BACKEND_URL } from "../lib/api";
import { ArrowLeft, ArrowRight, Quote, Sparkles } from "lucide-react";
import { toast } from "sonner";

function toAbs(u) {
  if (!u) return null;
  return u.startsWith("http") ? u : `${BACKEND_URL}${u}`;
}

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const [cs, setCs] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/case-studies/${slug}`),
      api.get("/case-studies"),
    ])
      .then(([c, all]) => {
        setCs(c.data);
        setRelated(all.data.filter((x) => x.slug !== slug).slice(0, 3));
      })
      .catch(() => toast.error("Case study not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen grain">
        <Navbar />
        <div className="max-w-3xl mx-auto p-10 text-zinc-500 animate-pulse">Loading…</div>
      </div>
    );
  }
  if (!cs) return null;

  const avgRating = cs.review_ratings
    ? Object.values(cs.review_ratings).reduce((a, b) => a + (b || 0), 0) / 5
    : 0;

  return (
    <div className="min-h-screen grain">
      <SEO
        title={cs.title}
        description={cs.headline}
        path={`/case-studies/${cs.slug}`}
        image={toAbs(cs.cover_image) || undefined}
        type="article"
        keywords={[
          `${cs.brand_name} case study`,
          `${cs.creator_name} case study`,
          ...(cs.creator_niches || []).map((n) => `${n} case study`),
          "creator marketing case study",
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": cs.title,
          "description": cs.headline,
          "image": toAbs(cs.cover_image),
          "datePublished": cs.published_at,
          "author": { "@type": "Organization", "name": cs.brand_name },
          "publisher": {
            "@type": "Organization",
            "name": "Nexus",
            "logo": { "@type": "ImageObject", "url": "https://collabnexus.co.uk/nexus-logo.png" },
          },
          "mainEntityOfPage": `${window.location.origin}/case-studies/${cs.slug}`,
        }}
      />
      <Navbar />

      <div className="relative overflow-hidden">
        <div className="absolute inset-0 h-[420px] overflow-hidden">
          {cs.cover_image ? (
            <img src={toAbs(cs.cover_image)} alt="" className="w-full h-full object-cover opacity-40" />
          ) : (
            <div className="w-full h-full nexus-gradient opacity-30" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#060608]/60 to-[#060608]" />
        </div>

        <article className="max-w-3xl mx-auto px-6 lg:px-10 pt-14 pb-16 relative">
          <Link to="/case-studies" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-8" data-testid="back-to-case-studies">
            <ArrowLeft className="h-4 w-4" /> All case studies
          </Link>

          <div className="text-xs uppercase tracking-[0.3em] text-nexus-blue mb-3 flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" /> {cs.brand_name}
          </div>
          <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tighter leading-tight mb-4" data-testid="case-study-title">
            {cs.title}
          </h1>
          <p className="text-lg text-zinc-300 leading-relaxed mb-8">{cs.headline}</p>

          <div className="flex flex-wrap items-center gap-4 mb-10">
            <Link to={`/creators/${cs.creator_id}`} className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full overflow-hidden hairline bg-black/40">
                {cs.creator_avatar && <img src={toAbs(cs.creator_avatar)} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="text-sm">
                <div className="font-semibold hover:underline">{cs.creator_name}</div>
                <div className="flex items-center gap-2">
                  <BadgeTier tier={cs.creator_badge_tier || "bronze"} size="sm" />
                  <StarRating value={avgRating} size="sm" />
                </div>
              </div>
            </Link>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-10" data-testid="case-study-metrics">
            {(cs.metrics || []).slice(0, 3).map((m, i) => (
              <div key={i} className="p-4 rounded-2xl bg-[#0C0C10] hairline text-center">
                <div className="font-display font-black nexus-gradient-text text-2xl md:text-3xl leading-none truncate">{m.value}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-2">{m.label}</div>
              </div>
            ))}
          </div>

          <div className="glass rounded-2xl p-6 md:p-8 mb-10" data-testid="case-study-narrative">
            <div className="text-zinc-100 leading-relaxed whitespace-pre-wrap text-base md:text-lg">
              {cs.narrative}
            </div>
          </div>

          {cs.quote && (
            <div className="my-12 p-6 md:p-8 rounded-2xl hairline bg-[#0C0C10] relative" data-testid="case-study-quote">
              <Quote className="h-8 w-8 text-nexus-purple absolute -top-4 -left-2 rotate-12" />
              <p className="font-display text-xl md:text-2xl italic text-zinc-100 leading-relaxed">&ldquo;{cs.quote}&rdquo;</p>
              <div className="text-sm text-zinc-400 mt-4">— {cs.quote_author}</div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 mb-8">
            {(cs.creator_niches || []).map((n) => (
              <span key={n} className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border border-white/10 bg-white/[0.02] text-zinc-300">{n}</span>
            ))}
            {(cs.creator_platforms || []).map((p) => (
              <span key={p} className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border border-nexus-blue/30 bg-nexus-blue/10 text-nexus-blue">{p}</span>
            ))}
          </div>

          <div className="mt-14 glass rounded-2xl p-8 text-center">
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Want the same result?</div>
            <div className="font-display font-bold text-2xl mb-4">Hire {cs.creator_name} on Nexus.</div>
            <div className="flex gap-3 justify-center flex-wrap">
              <Link to={`/creators/${cs.creator_id}`} className="nexus-gradient text-white text-sm font-semibold px-5 h-11 rounded-full inline-flex items-center gap-2 hover:opacity-90">
                View profile <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/campaigns/new" className="bg-white/5 border border-white/15 text-white text-sm font-semibold px-5 h-11 rounded-full inline-flex items-center hover:bg-white/10">
                Post your own brief
              </Link>
            </div>
          </div>
        </article>
      </div>

      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14 border-t border-white/5">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-6">More case studies</div>
          <div className="grid md:grid-cols-3 gap-5">
            {related.map((c) => (
              <Link key={c.slug} to={`/case-studies/${c.slug}`} className="rounded-2xl overflow-hidden hairline bg-[#0C0C10] card-hover">
                <div className="aspect-[16/9] overflow-hidden">
                  {c.cover_image ? (
                    <img src={toAbs(c.cover_image)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full nexus-gradient opacity-30" />
                  )}
                </div>
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-widest text-nexus-blue mb-2">{c.brand_name}</div>
                  <div className="font-display font-bold text-lg leading-tight">{c.title}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
